import React, { useState, useEffect, useContext, useRef } from "react";
import auth0, {
  WebAuth,
  Auth0DecodedHash,
  Auth0UserProfile,
  Auth0Callback,
} from "auth0-js";
import { UserProvider } from "../context/UserContext";
import UserInfo from "../api/UserInfo";
import { useHistory, useLocation } from "react-router";
import { Api } from "../api";
import { Routes } from "../constants/Routes";
import Repository from "../api/Repository";
import { CompanyContext } from "../context/CompanyContext";
import { Roles } from "../constants/Roles";
import Employee from "../types/Employee";
import { hasError } from "../helpers/ResponseHelper";
import * as Sentry from "@sentry/browser";

const auth: WebAuth = new auth0.WebAuth({
  domain: process.env.REACT_APP_AUTH0_DOMAIN as string,
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID as string,
  redirectUri: process.env.REACT_APP_AUTH0_CALLBACK_URL as string,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE as string,
  responseType: "token id_token",
  scope: "openid profile email",
});

const initState = {
  authenticated: false,
  profile: {} as Auth0UserProfile,
  accessToken: "",
  loginError: "",
  role: "",
  companyId: "",
  tosRequired: false,
  attributes: {} as Employee,
};

interface IProps {
  children: any;
}
export default function User(props: IProps) {
  const APP_STATE_LOGGED_IN: string = "EMPATHI_APP_STATE_LOGGED_IN";
  const APP_PATH_REDIRECT: string = "EMPATHI_APP_PATH_REDIRECT";

  var tokenRenewalTimeout: any;

  const [state, setState] = useState(initState);
  const ssoRef = useRef(false);
  const companyContext = useContext(CompanyContext);
  const history = useHistory();
  const location = useLocation();
  const redirectRoute = "/login";

  useEffect(() => {
    if (
      !state.authenticated &&
      localStorage.getItem(APP_STATE_LOGGED_IN) === "true"
    ) {
      localStorage.setItem(APP_PATH_REDIRECT, location.pathname);
      checkSession();
    } else {
      history.replace(redirectRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.accessToken && state.companyId) {
      Api.companies.getOne(state.companyId).then((res) => {
        if (!hasError(res)) {
          let company = res.data.data;
          companyContext.setCompanyState(
            company.companyId,
            company.repeatOnlyMissedQuestion,
            company.ssoFlg
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  function initiateLogin() {
    auth.authorize();
  }

  function checkSession() {
    auth.checkSession(
      {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE as string,
        responseType: "token id_token",
        scope: "openid profile email",
      },
      handleCheckSessionAuthResult
    );
  }

  function renewTokens(ssoVal: boolean) {
    if (ssoVal) {
      Api.sso
        .refreshToken()
        .then((result) => {
          const data = result.data.data;
          Repository.defaults.headers[
            "Authorization"
          ] = `Bearer ${data.access_token}`;
          scheduleRenewal();
        })
        .catch((error) => {
          setState(initState);
          console.log(error);
          localStorage.clear();
          history.replace(redirectRoute);
        });
    } else {
      auth.checkSession(
        {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE as string,
          responseType: "token id_token",
          scope: "openid profile email",
        },
        handleRenewAuthResult
      );
    }
  }

  function login(username: string, password: string) {
    setState(initState);
    auth.login(
      {
        realm: "Username-Password-Authentication",
        email: username,
        password: password,
      },
      handleAuthResult
    );
  }

  function handleRenewAuthResult(error: any, authResult: any) {
    if (error) {
      setState(initState);
      console.log(error);
      console.log(`Error ${error.error}: ${error.error_description}`);
      localStorage.clear();
      history.replace(redirectRoute);
    } else if (authResult && authResult.accessToken && authResult.idToken) {
      setSession(authResult, "renewing");
    }
  }

  function handleCheckSessionAuthResult(error: any, authResult: any) {
    if (error) {
      setState(initState);
      console.log(error);
      console.log(`Error ${error.error}: ${error.error_description}`);
      localStorage.clear();
      history.replace(redirectRoute);
    } else if (authResult && authResult.accessToken && authResult.idToken) {
      setSession(authResult, "checkSession");
    }
  }

  function handleAuthResult(error: any, authResult: any) {
    if (error) {
      setState({
        ...initState,
        loginError: error.description as string,
      });
      console.log(error);
      console.log(`Error ${error.error}: ${error.error_description}`);
      localStorage.clear();
    } else if (authResult && authResult.accessToken && authResult.idToken) {
      setSession(authResult, "login");
    }
  }

  function logout() {
    setState(initState);
    localStorage.clear();
    clearTimeout(tokenRenewalTimeout);
    auth.logout({ returnTo: process.env.REACT_APP_BASE_URL });
  }

  function handleAuthentication(ssoLogin: boolean) {
    ssoRef.current = ssoLogin;
    auth.parseHash(
      { __enableIdPInitiatedLogin: true },
      (error, authResult: any) => {
        if (error) {
          setState({
            ...initState,
            loginError: error.errorDescription as string,
          });
          history.replace(redirectRoute);
        } else if (authResult && authResult.accessToken && authResult.idToken) {
          setSession(authResult, "login");
        }
      }
    );
  }

  function setSession(authResult: Auth0DecodedHash, status: string) {
    if (status === "login") {
      history.replace("/loading-user");
    }

    let idTokenPayload: Auth0UserProfile = authResult.idTokenPayload;

    Repository.defaults.headers[
      "Authorization"
    ] = `Bearer ${authResult.accessToken}`;
    UserInfo.id = idTokenPayload.sub;

    if (status !== "renewing") {
      Api.employees
        .getByUserId(
          UserInfo.id,
          idTokenPayload.nickname,
          idTokenPayload.email_verified
        )
        .then((result) => {
          const employee = result.data.data;
          if (!employee.active) {
            setState({
              ...initState,
              loginError: "You are not authorized to use this app",
            });
          } else {
            UserInfo.attributes = employee;
            UserInfo.isAdmin =
              UserInfo.attributes.userRole === Roles.Admin ||
              UserInfo.attributes.userRole === Roles.Tenant ||
              UserInfo.attributes.userRole === Roles.SuperAdmin;
            Repository.defaults.headers["EmployeeId"] = employee.employeeId;

            setState({
              authenticated: true,
              profile: idTokenPayload,
              accessToken: authResult.accessToken as string,
              loginError: "",
              role: employee.userRole,
              companyId: employee.companyId,
              tosRequired: employee.tosRequired,
              attributes: employee,
            });

            localStorage.setItem(APP_STATE_LOGGED_IN, "true");

            //Set User Info for Heap Analytics
            (window as any).heap.identify(employee.employeeId);
            (window as any).heap.addUserProperties({
              companyId: employee.companyId,
            });

            Sentry.configureScope((scope) => {
              scope.setUser({
                id: employee.employeeId,
                companyId: employee.companyId,
              });
            });

            // Set user ID for LogRocket account
            (window as any).LogRocket.identify(employee.employeeId);

            scheduleRenewal();

            if (idTokenPayload.email_verified) {
              let redirectTo = localStorage.getItem(APP_PATH_REDIRECT);
              if (redirectTo) {
                history.replace(redirectTo);
              } else {
                history.replace(Routes.dashboard);
              }
            } else {
              history.replace(redirectRoute);
            }
          }
        })
        .catch((error) => {
          console.log(error);
          history.replace(redirectRoute);
          setState({
            ...initState,
            loginError: "There was a problem logging you in.",
          });
          localStorage.clear();
          clearTimeout(tokenRenewalTimeout);
        });
    } else {
      scheduleRenewal();
    }
  }

  function forgotPassword(email: string, callback: Auth0Callback<any>) {
    auth.changePassword(
      {
        connection: "Username-Password-Authentication",
        email: email,
      },
      callback
    );
  }

  function scheduleRenewal() {
    const timeout = 15 * 60000;
    tokenRenewalTimeout = setTimeout(() => {
      renewTokens(ssoRef.current);
    }, timeout);
  }

  function updateTos() {
    Api.employees.updateTos(state.attributes.employeeId).then((res) => {
      if (!hasError(res)) {
        setState({
          ...state,
          tosRequired: false,
        });
        history.push(Routes.dashboard);
      }
    });
  }

  const userProviderValue = {
    ...state,
    initiateLogin: initiateLogin,
    handleAuthentication: handleAuthentication,
    logout: logout,
    login: login,
    forgotPassword: forgotPassword,
    updateTos: updateTos,
  };

  return (
    <UserProvider value={userProviderValue}>{props.children}</UserProvider>
  );
}
